import { __assign, __read, __spread, __values } from "tslib";
import { IdentifyOperation, SpecialEventType } from '@amplitude/types';
import { logger, generateBase36Id } from '@amplitude/utils';
import { UNSET_VALUE, USER_IDENTIFY_OPERATIONS, GROUP_IDENTIFY_OPERATIONS } from './constants'; // A specific helper for the identify field

var identifyWarn = function identifyWarn(operation) {
  var msgs = [];

  for (var _i = 1; _i < arguments.length; _i++) {
    msgs[_i - 1] = arguments[_i];
  }

  return logger.warn.apply(logger, __spread(['On Identify operation ', operation, ': '], msgs));
};

var Identify =
/** @class */
function () {
  function Identify() {
    // The set of operations that have been added to this identify
    this._propertySet = new Set();
    this._properties = {};
    this._groups = {};
  }
  /** Create a user identify event out of this identify */


  Identify.prototype.identifyUser = function (userId, deviceId) {
    if (deviceId === void 0) {
      deviceId = null;
    }

    var identifyEvent = {
      event_type: SpecialEventType.IDENTIFY,
      groups: __assign({}, this._groups),
      user_properties: this.getUserProperties(),
      user_id: userId
    };

    if (deviceId !== null && deviceId.length > 0) {
      identifyEvent.device_id = deviceId;
    }

    return identifyEvent;
  };

  Identify.prototype.identifyGroup = function (groupName, groupValue) {
    var _a;

    var identifyEvent = {
      event_type: SpecialEventType.GROUP_IDENTIFY,
      groups: (_a = {}, _a[groupName] = groupValue, _a),
      user_properties: this.getGroupUserProperties(),
      device_id: generateBase36Id()
    };
    return identifyEvent;
  };

  Identify.prototype.getUserProperties = function () {
    var e_1, _a;

    var userPropertiesCopy = {};

    try {
      for (var USER_IDENTIFY_OPERATIONS_1 = __values(USER_IDENTIFY_OPERATIONS), USER_IDENTIFY_OPERATIONS_1_1 = USER_IDENTIFY_OPERATIONS_1.next(); !USER_IDENTIFY_OPERATIONS_1_1.done; USER_IDENTIFY_OPERATIONS_1_1 = USER_IDENTIFY_OPERATIONS_1.next()) {
        var field = USER_IDENTIFY_OPERATIONS_1_1.value;

        if (field in this._properties) {
          userPropertiesCopy[field] = this._properties[field];
        }
      }
    } catch (e_1_1) {
      e_1 = {
        error: e_1_1
      };
    } finally {
      try {
        if (USER_IDENTIFY_OPERATIONS_1_1 && !USER_IDENTIFY_OPERATIONS_1_1.done && (_a = USER_IDENTIFY_OPERATIONS_1.return)) _a.call(USER_IDENTIFY_OPERATIONS_1);
      } finally {
        if (e_1) throw e_1.error;
      }
    }

    return userPropertiesCopy;
  };

  Identify.prototype.getGroupUserProperties = function () {
    var e_2, _a;

    var userPropertiesCopy = {};

    try {
      for (var GROUP_IDENTIFY_OPERATIONS_1 = __values(GROUP_IDENTIFY_OPERATIONS), GROUP_IDENTIFY_OPERATIONS_1_1 = GROUP_IDENTIFY_OPERATIONS_1.next(); !GROUP_IDENTIFY_OPERATIONS_1_1.done; GROUP_IDENTIFY_OPERATIONS_1_1 = GROUP_IDENTIFY_OPERATIONS_1.next()) {
        var field = GROUP_IDENTIFY_OPERATIONS_1_1.value;

        if (field in this._properties) {
          userPropertiesCopy[field] = this._properties[field];
        }
      }
    } catch (e_2_1) {
      e_2 = {
        error: e_2_1
      };
    } finally {
      try {
        if (GROUP_IDENTIFY_OPERATIONS_1_1 && !GROUP_IDENTIFY_OPERATIONS_1_1.done && (_a = GROUP_IDENTIFY_OPERATIONS_1.return)) _a.call(GROUP_IDENTIFY_OPERATIONS_1);
      } finally {
        if (e_2) throw e_2.error;
      }
    }

    return userPropertiesCopy;
  };

  Identify.prototype.setGroup = function (groupName, groupValue) {
    var isSuccessfulSet = this._safeSet(IdentifyOperation.SET, groupName, groupValue);

    if (isSuccessfulSet) {
      this._groups[groupName] = groupValue;
    }

    return this;
  };

  Identify.prototype.set = function (property, value) {
    this._safeSet(IdentifyOperation.SET, property, value);

    return this;
  };

  Identify.prototype.setOnce = function (property, value) {
    this._safeSet(IdentifyOperation.SET_ONCE, property, value);

    return this;
  };

  Identify.prototype.append = function (property, value) {
    this._safeSet(IdentifyOperation.APPEND, property, value);

    return this;
  };

  Identify.prototype.prepend = function (property, value) {
    this._safeSet(IdentifyOperation.PREPEND, property, value);

    return this;
  };

  Identify.prototype.postInsert = function (property, value) {
    this._safeSet(IdentifyOperation.POSTINSERT, property, value);

    return this;
  };

  Identify.prototype.preInsert = function (property, value) {
    this._safeSet(IdentifyOperation.PREINSERT, property, value);

    return this;
  };

  Identify.prototype.remove = function (property, value) {
    this._safeSet(IdentifyOperation.REMOVE, property, value);

    return this;
  };

  Identify.prototype.add = function (property, value) {
    this._safeSet(IdentifyOperation.ADD, property, value);

    return this;
  };

  Identify.prototype.unset = function (property) {
    this._safeSet(IdentifyOperation.UNSET, property, UNSET_VALUE);

    return this;
  };

  Identify.prototype.clearAll = function () {
    // When clear all happens, all properties are unset. Reset the entire object.
    this._properties = {};
    this._properties[IdentifyOperation.CLEAR_ALL] = UNSET_VALUE;
    return this;
  }; // Returns whether or not this set actually worked.


  Identify.prototype._safeSet = function (operation, property, value) {
    if (this._validate(operation, property, value)) {
      var userPropertyMap = this._properties[operation];

      if (userPropertyMap === undefined) {
        userPropertyMap = {};
        this._properties[operation] = userPropertyMap;
      }

      userPropertyMap[property] = value;

      this._propertySet.add(property);

      return true;
    }

    return false;
  };

  Identify.prototype._validate = function (operation, property, value) {
    var e_3, _a;

    if (this._properties[IdentifyOperation.CLEAR_ALL] !== undefined) {
      identifyWarn(operation, 'clear all already set. Skipping operation');
      return false;
    }

    if (typeof property !== 'string') {
      identifyWarn(operation, 'expected string for property but got: ', typeof property, '. Skipping operation');
      return false;
    }

    if (this._propertySet.has(property)) {
      identifyWarn(operation, 'property ', property, ' already used. Skipping operation');
      return false;
    }

    if (operation === IdentifyOperation.ADD) {
      return typeof value === 'number';
    } else if (operation !== IdentifyOperation.UNSET && operation !== IdentifyOperation.REMOVE) {
      if (Array.isArray(value)) {
        try {
          for (var value_1 = __values(value), value_1_1 = value_1.next(); !value_1_1.done; value_1_1 = value_1.next()) {
            var valueElement = value_1_1.value;

            if (!(typeof valueElement === 'number' || typeof valueElement === 'string')) {
              identifyWarn(operation, 'invalid array element type ', typeof valueElement, '. Skipping operation');
              return false;
            }
          }
        } catch (e_3_1) {
          e_3 = {
            error: e_3_1
          };
        } finally {
          try {
            if (value_1_1 && !value_1_1.done && (_a = value_1.return)) _a.call(value_1);
          } finally {
            if (e_3) throw e_3.error;
          }
        }
      } else if (!(typeof value === 'number' || typeof value === 'string')) {
        identifyWarn(operation, 'invalid value type ', typeof value, '. Skipping operation');
        return false;
      }
    }

    return true;
  };

  return Identify;
}();

export { Identify };